import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const pluralShape = {
	type: 'object',
	shape: {
		zero: { type: 'string', optional: true },
		one: { type: 'string', optional: true },
		two: { type: 'string', optional: true },
		few: { type: 'string', optional: true },
		many: { type: 'string', optional: true },
		other: { type: 'string' },
	},
} as const;

// Static marketing content, not derived from user/transaction data — pre-baked per locale rather
// than runtime-formatted (per CHANGE-98339's stat-tiles localization spec).
const statsShape = {
	type: 'object',
	shape: {
		stat_1_value: { type: 'string' },
		stat_1_label: { type: 'string' },
		stat_2_value: { type: 'string' },
		stat_2_label: { type: 'string' },
		stat_3_value: { type: 'string' },
		stat_3_label: { type: 'string' },
		stat_4_value: { type: 'string' },
		stat_4_label: { type: 'string' },
	},
} as const;

export const FCM_PPMU_THANKYOU_CLARITY_GLOBAL = defineFcm('ppmu_thankyou_clarity_global')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				enabled: { type: 'boolean' },
				experiment_name: { type: 'string' },
				corgi: {
					type: 'object',
					optional: true,
					shape: {
						co: {
							type: 'object',
							shape: {
								ppmu: {
									type: 'object',
									shape: {
										thank_you: {
											type: 'object',
											shape: {
												share_button_label: { type: 'string' },
												hero_title_v1: { type: 'string' },
												hero_title_v1_guest: { type: 'string' },
												hero_text_v1: { type: 'string' },
												stats_intro: { type: 'string' },
												transaction_section_label: { type: 'string' },
												victories_heading: { type: 'string' },
												closing_title: { type: 'string' },
												closing_text: { type: 'string' },
												transaction_card: {
													type: 'object',
													shape: {
														boost_row_title: { type: 'string' },
														boost_row_meta: { type: 'string' },
														membership_row_title: { type: 'string' },
														membership_row_meta: { type: 'string' },
														membership_row_meta_starts: { type: 'string' },
														next_payment_label: { type: 'string' },
														starts_label: { type: 'string' },
														renews_monthly_label: { type: 'string' },
														renews_monthly_value: { type: 'string' },
														reach_text_plural: pluralShape,
														contact_us: { type: 'string' },
													},
												},
												stats: statsShape,
											},
										},
									},
								},
							},
						},
					},
				},
			},
		}),
	)
	.withDefault({
		enabled: false,
		experiment_name: 'ppmu_thankyou_clarity_global',
		corgi: {
			co: {
				ppmu: {
					thank_you: {
						share_button_label: 'Share the petition I signed',
						hero_title_v1: 'Welcome to the movement, %{userPreferredName}',
						hero_title_v1_guest: 'Welcome to the movement!',
						hero_text_v1:
							'Your monthly contribution makes sure the causes you care about have a place to rally support.',
						stats_intro:
							'Your <strong>%{formattedMembershipAmount}/month</strong> funds the technology and team that keep this platform running.',
						transaction_section_label: "Here's what you contributed today:",
						victories_heading: 'All of these victories are possible thanks to members like you.',
						closing_title: 'Your contribution matters to real people fighting for real change.',
						closing_text:
							'Change.org runs entirely on members, with no ads and no outside owners. Your monthly contribution keeps the platform independent and here for the long haul.',
						transaction_card: {
							boost_row_title: 'Boost the petition',
							boost_row_meta: 'one-time',
							membership_row_title: 'Change.org membership',
							membership_row_meta: 'month 1',
							membership_row_meta_starts: 'starts %{formattedMembershipStartDate}',
							next_payment_label: 'Next payment',
							starts_label: 'First payment',
							renews_monthly_label: 'Renews monthly',
							renews_monthly_value: '%{formattedMembershipAmount}/mo',
							reach_text_plural: {
								one: 'Your <strong>%{formattedPromotionAmount}</strong> helps %{impressions} more person see the petition.',
								other:
									'Your <strong>%{formattedPromotionAmount}</strong> helps %{impressions} more people see the petition.',
							},
							contact_us: 'Questions? Reach us at <a href="mailto:%{email}">%{email}</a>',
						},
						stats: {
							stat_1_value: '586M',
							stat_1_label: 'people signing and starting petitions',
							stat_2_value: 'Hourly',
							stat_2_label: 'a victory won around the world',
							stat_3_value: '196',
							stat_3_label: 'countries where change is happening',
							stat_4_value: '100%',
							stat_4_label: 'member-funded, by people like you',
						},
					},
				},
			},
		},
	})
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'PPMU thank-you page',
		supportedGroups: { locales: true },
		description:
			'Per-locale translated copy, rollout switch, and Experiment Service experiment name for the PPMU thank-you page Variant 1 transaction accordion (CHANGE-98339). `enabled` is checked per-locale — each locale override controls its own rollout independently, there is no single global switch. `experiment_name` lets a locale (or any override tier) point at a different Experiment Service experiment without a code change. corgi.en-US.yml defines the same keys as a base fallback for every locale (including en-US); useFcTranslate() makes this config win over that base wherever a locale actually has a value.',
		template: {
			enabled: true,
			experiment_name: '',
			corgi: {
				co: {
					ppmu: {
						thank_you: {
							share_button_label: '',
							hero_title_v1: '',
							hero_title_v1_guest: '',
							hero_text_v1: '',
							stats_intro: '',
							transaction_section_label: '',
							victories_heading: '',
							closing_title: '',
							closing_text: '',
							transaction_card: {
								boost_row_title: '',
								boost_row_meta: '',
								membership_row_title: '',
								membership_row_meta: '',
								membership_row_meta_starts: '',
								next_payment_label: '',
								starts_label: '',
								renews_monthly_label: '',
								renews_monthly_value: '',
								reach_text_plural: { zero: '', one: '', two: '', few: '', many: '', other: '' },
								contact_us: '',
							},
							stats: {
								stat_1_value: '',
								stat_1_label: '',
								stat_2_value: '',
								stat_2_label: '',
								stat_3_value: '',
								stat_3_label: '',
								stat_4_value: '',
								stat_4_label: '',
							},
						},
					},
				},
			},
		},
	});
